import { Link } from 'react-router-dom';
import type { SessionRecord } from '@/api/sessions';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';

function formatTime(h: number, m: number): string {
    const hh = (h % 12) || 12;
    const mm = String(m).padStart(2, '0');
    const ampm = h < 12 ? 'am' : 'pm';
    return `${hh}:${mm} ${ampm}`;
}

function noteStatusNote(status: string, noteStatus: string): { text: string; tone: 'warn' | 'info' | 'accent' } | null {
    if (status !== 'Complete') return null;
    if (noteStatus === '') return { text: 'Note not started', tone: 'warn' };
    if (noteStatus === 'Drafting') return { text: 'Drafting', tone: 'info' };
    if (noteStatus === 'Complete') return { text: 'Ready to finalize', tone: 'accent' };
    if (noteStatus === 'Submitted') return { text: 'Awaiting parent signature', tone: 'info' };
    return null;
}

export function TodayTimeline({ sessions }: { sessions: SessionRecord[] }) {
    const sorted = [...sessions].sort(
        (a, b) => (a.startH * 60 + a.startM) - (b.startH * 60 + b.startM),
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>Today's schedule</CardTitle>
            </CardHeader>
            {sorted.length === 0 ? (
                <p className="text-sm text-text-subtle py-4 text-center">
                    No sessions scheduled for today.
                </p>
            ) : (
                <ul className="flex flex-col">
                    {sorted.map((s) => {
                        const hint = noteStatusNote(s.status, s.noteStatus);
                        const hintColor =
                            hint?.tone === 'warn' ? 'text-warning-text'
                          : hint?.tone === 'info' ? 'text-info-text'
                          : 'text-accent-text';
                        return (
                            <li
                                key={s.id}
                                className="flex gap-3 py-2 border-b border-border last:border-b-0 items-center"
                            >
                                <div className="w-20 shrink-0 text-xs text-text-subtle tabular-nums">
                                    {formatTime(s.startH, s.startM)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Link
                                            to={`/scheduling?session=${s.id}`}
                                            className="font-bold text-sm text-text hover:text-accent-text truncate"
                                        >
                                            {s.client || '—'}
                                        </Link>
                                        <StatusBadge status={s.status} />
                                        {hint && (
                                            <span className={`text-[11px] ${hintColor}`}>
                                                {hint.text}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-text-subtle">
                                        {s.staff || '—'} · {s.label} · {(s.dur / 60).toFixed(1)}h
                                        {s.location ? ` · ${s.location}` : ''}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Card>
    );
}
